import React, { useState, useEffect } from 'react';
import {
  HiOutlinePlus,
  HiOutlineMagnifyingGlass,
  HiOutlineDocument,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineXMark
} from 'react-icons/hi2';
import { authService, machineService } from '../../../services';
import masterDataService from '../../../services/masterDataService';
import SharedModal from '../../../components/common/SharedModal';
import PageLoading from "../../../components/common/PageLoading";
import "../PlannedMaintenanceMasters.css";

const SensorTagsPage = () => {
  const [tags, setTags] = useState([]);
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({
    machineId: '',
    tagName: '',
    opcAddress: '',
    mqttTopic: '',
    dataType: '',
    pollingIntervalSec: '',
    description: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const tenantId = authService.getTenantId();

  useEffect(() => {
    if (tenantId) {
      loadData();
    } else {
      setError('Please login to view sensor tags');
      setLoading(false);
    }
  }, [tenantId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      const [tagsData, machinesData] = await Promise.all([
        masterDataService.getSensorTags(tenantId),
        machineService.getMachines(tenantId)
      ]);
      setTags(tagsData || []);
      setMachines(machinesData || []);
    } catch (err) {
      setError('Failed to load data. Please try again.');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredTags = tags.filter((tag) =>
    tag.tagName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tag.dataType?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingTag(null);
    setFormData({
      machineId: '',
      tagName: '',
      opcAddress: '',
      mqttTopic: '',
      dataType: '',
      pollingIntervalSec: '',
      description: '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setFormData({
      machineId: tag.machineId?.toString() || '',
      tagName: tag.tagName || '',
      opcAddress: tag.opcAddress || '',
      mqttTopic: tag.mqttTopic || '',
      dataType: tag.dataType || '',
      pollingIntervalSec: tag.pollingIntervalSec?.toString() || '',
      description: tag.description || '',
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    try {
      setError('');
      await masterDataService.deleteSensorTag(id);
      await loadData();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete sensor tag. Please try again.');
      console.error('Error deleting sensor tag:', err);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.machineId) {
      errors.machineId = 'Machine is required';
    }
    if (!formData.tagName.trim()) {
      errors.tagName = 'Tag name is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      const tagData = {
        tenantId: tenantId,
        machineId: parseInt(formData.machineId),
        tagName: formData.tagName.trim(),
        opcAddress: formData.opcAddress.trim() || null,
        mqttTopic: formData.mqttTopic.trim() || null,
        dataType: formData.dataType.trim() || null,
        pollingIntervalSec: formData.pollingIntervalSec ? parseInt(formData.pollingIntervalSec) : null,
        description: formData.description.trim() || null,
      };

      if (editingTag) {
        tagData.tagId = editingTag.tagId;
        await masterDataService.updateSensorTag(editingTag.tagId, tagData);
      } else {
        await masterDataService.createSensorTag(tagData);
      }

      await loadData();
      setShowModal(false);
      setFormData({ machineId: '', tagName: '', opcAddress: '', mqttTopic: '', dataType: '', pollingIntervalSec: '', description: '' });
      setEditingTag(null);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.errors || 
                          err.message || 
                          'Failed to save sensor tag. Please try again.';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTag(null);
    setFormData({ machineId: '', tagName: '', opcAddress: '', mqttTopic: '', dataType: '', pollingIntervalSec: '', description: '' });
    setFormErrors({});
  };

  const getMachineName = (machineId) => {
    if (!machineId) return '-';
    const machine = machines.find(m => m.machineId === machineId);
    return machine ? `${machine.machineCode} - ${machine.machineName}` : '-';
  };

  if (loading) {
    return (
      <div className="pm-masters-page">
        <PageLoading message="Loading sensor tags..." />
      </div>
    );
  }

  return (
    <div className="pagecontainer pm-masters-page">
      <div className="page-header">
        <div className="header-content">
          <h1>Sensor Tags</h1>
          <p>Manage sensor tags for Planned Maintenance</p>
        </div>
        <button className="btn-primary" onClick={handleAdd}>
          <HiOutlinePlus />
          Add Tag
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="table-container">
        <div className="table-header">
          <div className="search-box">
            <HiOutlineMagnifyingGlass className="search-icon" />
            <input
              type="text"
              placeholder="Search tags..."
              value={searchTerm}
              onChange={handleSearch}
              className="search-input"
            />
          </div>
          <div className="table-info">
            {filteredTags.length} {filteredTags.length === 1 ? 'tag' : 'tags'}
          </div>
        </div>

        {filteredTags.length === 0 ? (
          <div className="empty-state">
            <HiOutlineDocument className="empty-icon" />
            <h3>No tags found</h3>
            <p>
              {searchTerm
                ? 'Try adjusting your search terms'
                : 'Get started by creating your first sensor tag'}
            </p>
            {!searchTerm && (
              <button className="btn-primary" onClick={handleAdd}>
                Add Tag
              </button>
            )}
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Machine</th>
                <th>Tag Name</th>
                <th>Data Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTags.map((tag) => (
                <tr key={tag.tagId}>
                  <td>{tag.tagId}</td>
                  <td>{getMachineName(tag.machineId)}</td>
                  <td className="name-cell">{tag.tagName}</td>
                  <td>{tag.dataType || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon-edit"
                        onClick={() => handleEdit(tag)}
                        title="Edit"
                      >
                        <HiOutlinePencil />
                      </button>
                      <button
                        className="btn-icon-delete"
                        onClick={() => setDeleteConfirm(tag)}
                        title="Delete"
                      >
                        <HiOutlineTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create/Edit Modal */}
      <SharedModal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingTag ? 'Edit Sensor Tag' : 'Add New Sensor Tag'}
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={handleCloseModal}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="sensor-tag-form"
              className="btn-primary"
              disabled={submitting}
            >
              {submitting
                ? editingTag
                  ? 'Updating...'
                  : 'Creating...'
                : editingTag
                ? 'Update'
                : 'Create'}
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <form id="sensor-tag-form" onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="machineId">
                Machine <span className="required">*</span>
              </label>
              <select
                id="machineId"
                value={formData.machineId}
                onChange={(e) =>
                  setFormData({ ...formData, machineId: e.target.value })
                }
                className={formErrors.machineId ? 'error' : ''}
              >
                <option value="">Select Machine</option>
                {machines.map((machine) => (
                  <option key={machine.machineId} value={machine.machineId.toString()}>
                    {machine.machineCode} - {machine.machineName}
                  </option>
                ))}
              </select>
              {formErrors.machineId && (
                <span className="field-error">{formErrors.machineId}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="tagName">
                Tag Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="tagName"
                value={formData.tagName}
                onChange={(e) =>
                  setFormData({ ...formData, tagName: e.target.value })
                }
                className={formErrors.tagName ? 'error' : ''}
                placeholder="Enter tag name"
              />
              {formErrors.tagName && (
                <span className="field-error">{formErrors.tagName}</span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="opcAddress">OPC Address</label>
              <input
                type="text"
                id="opcAddress"
                value={formData.opcAddress}
                onChange={(e) =>
                  setFormData({ ...formData, opcAddress: e.target.value })
                }
                placeholder="Enter OPC address (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mqttTopic">MQTT Topic</label>
              <input
                type="text"
                id="mqttTopic"
                value={formData.mqttTopic}
                onChange={(e) =>
                  setFormData({ ...formData, mqttTopic: e.target.value })
                }
                placeholder="Enter MQTT topic (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="dataType">Data Type</label>
              <input
                type="text"
                id="dataType"
                value={formData.dataType}
                onChange={(e) =>
                  setFormData({ ...formData, dataType: e.target.value })
                }
                placeholder="Enter data type (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="pollingIntervalSec">Polling Interval (sec)</label>
              <input
                type="number"
                id="pollingIntervalSec"
                value={formData.pollingIntervalSec}
                onChange={(e) =>
                  setFormData({ ...formData, pollingIntervalSec: e.target.value })
                }
                placeholder="Enter polling interval (optional)"
              />
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="Enter description (optional)"
                rows="3"
              />
            </div>

          </form>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        customClass="small"
        footer={
          <>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-danger"
              onClick={() => handleDelete(deleteConfirm.tagId)}
            >
              Delete
            </button>
          </>
        }
      >
        <div className="shared-modal-body">
          <p>
            Are you sure you want to delete{' '}
            <strong>{deleteConfirm?.tagName}</strong>?
          </p>
          <p className="warning-text">
            This action cannot be undone.
          </p>
        </div>
      </SharedModal>
    </div>
  );
};

export default SensorTagsPage;
